import { useLayoutEffect } from 'react';

import {
  useGetDictionaryAttendanceReasonQuery,
  useGetDictionaryAttendanceStatusesQuery,
  useGetDictionaryAttendanceTypeQuery,
  useGetWorkLocationTypesQuery,
  useGetProjectNDepartmentsQuery,
  useGetDictionaryLeaveStatusesQuery,
  useGetDictionaryLeaveTypeQuery,
  useGetDictionaryITAccessoriesQuery,
  useGetDictionaryITReqItemsQuery,
  useGetDictionaryITReqStatusQuery,
  useGetDictionaryPassportStatusesQuery,
  useGetDictionaryPassportTypeQuery,
  useGetDictionarySalaryStatusesQuery,
  useGetDictionarySalaryTypeQuery,
  useGetDictionaryOverseasSponsorsQuery,
  useGetDictionaryOverseasStatusesQuery,
  useGetDictionaryResignationReasonsQuery,
  useGetDictionaryResignationStatusesQuery,
  useGetDictionaryResumptionStatusesQuery,
  useGetDeptProjListQuery,
  useGetOrganizationsQuery,
  useGetDictionaryResumptionWorkShiftQuery,
} from '@Store/api/dictionaryApiSlice';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { dictionaryActions } from '@Store/slices/dictionarySlice';
import RequestType from '@Enum/RequestTypes.enum';
import useRequestDictionaryLoader from './useRequestDictionaryLoader';

const useDictionaryLoader = (): void => {
  const requestName = useRequestDictionaryLoader();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);
  const projectsNDepartmentsDict = useGetProjectNDepartmentsQuery(
    currentUser?.empNumber,
    {
      skip: !currentUser?.empNumber,
    }
  );

  // Leave Request Dictionaries
  const leaveTypeDict = useGetDictionaryLeaveTypeQuery(undefined, {
    skip: requestName !== RequestType.LEAVE,
  });
  const leaveStatusesDict = useGetDictionaryLeaveStatusesQuery(undefined, {
    skip: requestName !== RequestType.LEAVE,
  });

  // Attendance Request Dictionaries
  const deptProjList = useGetDeptProjListQuery(undefined, {
    skip: requestName !== RequestType.ATTENDANCE,
  });
  const workLocationTypesDict = useGetWorkLocationTypesQuery(undefined, {
    skip: requestName !== RequestType.ATTENDANCE,
  });
  const attendanceReasonDict = useGetDictionaryAttendanceReasonQuery(
    undefined,
    {
      skip: requestName !== RequestType.ATTENDANCE,
    }
  );
  const attendanceStatusesDict = useGetDictionaryAttendanceStatusesQuery(
    undefined,
    {
      skip: requestName !== RequestType.ATTENDANCE,
    }
  );
  const attendanceTypeDict = useGetDictionaryAttendanceTypeQuery(undefined, {
    skip: requestName !== RequestType.ATTENDANCE,
  });

  // IT Requisition Request Dictionaries
  const itReqItemsDict = useGetDictionaryITReqItemsQuery(undefined, {
    skip: requestName !== RequestType.IT_REQUISITION,
  });
  const itAccessories = useGetDictionaryITAccessoriesQuery(undefined, {
    skip: requestName !== RequestType.IT_REQUISITION,
  });
  const itReqStatusesDict = useGetDictionaryITReqStatusQuery(undefined, {
    skip: requestName !== RequestType.IT_REQUISITION,
  });
  const organizationsDict = useGetOrganizationsQuery(undefined, {
    skip: requestName !== RequestType.IT_REQUISITION,
  });

  // Passport Request Dictionaries
  const passportTypeDict = useGetDictionaryPassportTypeQuery(undefined, {
    skip: requestName !== RequestType.PASSPORT,
  });
  const passportStatusesDict = useGetDictionaryPassportStatusesQuery(
    undefined,
    {
      skip: requestName !== RequestType.PASSPORT,
    }
  );

  // Salary Request Dictionaries
  const salaryStatusesDict = useGetDictionarySalaryStatusesQuery(undefined, {
    skip: requestName !== RequestType.SALARY,
  });
  const salaryTypeDict = useGetDictionarySalaryTypeQuery(undefined, {
    skip: requestName !== RequestType.SALARY,
  });

  // Overseas Request Dictionaries
  const overseasSponsorsDict = useGetDictionaryOverseasSponsorsQuery(
    undefined,
    {
      skip: requestName !== RequestType.OVERSEAS,
    }
  );
  const overseasStatusesDict = useGetDictionaryOverseasStatusesQuery(
    undefined,
    {
      skip: requestName !== RequestType.OVERSEAS,
    }
  );

  // Resignation Request Dictionaries
  const resignationReasonsDict = useGetDictionaryResignationReasonsQuery(
    undefined,
    {
      skip: requestName !== RequestType.RESIGNATION,
    }
  );
  const resignationStatusesDict = useGetDictionaryResignationStatusesQuery(
    undefined,
    {
      skip: requestName !== RequestType.RESIGNATION,
    }
  );

  // Resumption Request Dictionaries
  const resumptionStatusesDict = useGetDictionaryResumptionStatusesQuery(
    undefined,
    {
      skip: requestName !== RequestType.RESUMPTION,
    }
  );
  const resumptionWorkShiftDict = useGetDictionaryResumptionWorkShiftQuery(
    undefined,
    {
      skip: requestName !== RequestType.RESUMPTION,
    }
  );

  useLayoutEffect(() => {
    if (resumptionWorkShiftDict?.data) {
      dispatch(
        dictionaryActions.setResumptionWorkShiftDict(
          resumptionWorkShiftDict.data
        )
      );
    }
  }, [resumptionWorkShiftDict?.data]);

  useLayoutEffect(() => {
    if (resumptionStatusesDict?.data) {
      dispatch(
        dictionaryActions.setResumptionStatusesDict(resumptionStatusesDict.data)
      );
    }
  }, [resumptionStatusesDict?.data]);

  useLayoutEffect(() => {
    if (resignationReasonsDict?.data) {
      dispatch(
        dictionaryActions.setResignationReasonsDict(resignationReasonsDict.data)
      );
    }
  }, [resignationReasonsDict?.data]);

  useLayoutEffect(() => {
    if (resignationStatusesDict?.data) {
      dispatch(
        dictionaryActions.setResignationStatusesDict(
          resignationStatusesDict.data
        )
      );
    }
  }, [resignationStatusesDict?.data]);

  useLayoutEffect(() => {
    if (overseasSponsorsDict?.data) {
      dispatch(
        dictionaryActions.setOverseasSponsorsDict(overseasSponsorsDict.data)
      );
    }
  }, [overseasSponsorsDict.data]);

  useLayoutEffect(() => {
    if (overseasStatusesDict?.data) {
      dispatch(
        dictionaryActions.setOverseasStatusesDict(overseasStatusesDict.data)
      );
    }
  }, [overseasStatusesDict.data]);

  useLayoutEffect(() => {
    if (salaryTypeDict?.data) {
      dispatch(dictionaryActions.setSalaryTypeDict(salaryTypeDict.data));
    }
  }, [salaryTypeDict?.data]);

  useLayoutEffect(() => {
    if (salaryStatusesDict?.data) {
      dispatch(
        dictionaryActions.setSalaryStatusesDict(salaryStatusesDict.data)
      );
    }
  }, [salaryStatusesDict?.data]);

  useLayoutEffect(() => {
    if (passportTypeDict?.data) {
      dispatch(dictionaryActions.setPassportTypeDict(passportTypeDict.data));
    }
  }, [passportTypeDict?.data]);

  useLayoutEffect(() => {
    if (passportStatusesDict?.data) {
      dispatch(
        dictionaryActions.setPassportStatusesDict(passportStatusesDict.data)
      );
    }
  }, [passportStatusesDict?.data]);

  useLayoutEffect(() => {
    if (itReqStatusesDict.data) {
      dispatch(dictionaryActions.setITReqStatusesDict(itReqStatusesDict.data));
    }
  }, [itReqStatusesDict.data]);

  useLayoutEffect(() => {
    if (itReqItemsDict?.data) {
      dispatch(dictionaryActions.setITReqItemsDict(itReqItemsDict.data));
    }
  }, [itReqItemsDict?.data]);

  useLayoutEffect(() => {
    if (organizationsDict?.data) {
      dispatch(dictionaryActions.setOrganizationsDict(organizationsDict.data));
    }
  }, [organizationsDict?.data]);

  useLayoutEffect(() => {
    if (itAccessories?.data) {
      dispatch(dictionaryActions.setITAccessories(itAccessories.data));
    }
  }, [itAccessories?.data]);

  useLayoutEffect(() => {
    if (leaveTypeDict?.data) {
      dispatch(dictionaryActions.setLeaveTypeDict(leaveTypeDict.data));
    }
  }, [leaveTypeDict?.data]);

  useLayoutEffect(() => {
    if (leaveStatusesDict?.data) {
      dispatch(dictionaryActions.setLeaveStatusesDict(leaveStatusesDict.data));
    }
  }, [leaveStatusesDict?.data]);

  useLayoutEffect(() => {
    if (projectsNDepartmentsDict?.data) {
      dispatch(
        dictionaryActions.setProjectNDepartmentsDict(
          projectsNDepartmentsDict.data
        )
      );
    }
  }, [projectsNDepartmentsDict.data]);

  useLayoutEffect(() => {
    if (workLocationTypesDict?.data) {
      dispatch(
        dictionaryActions.setWorkLocationTypeDict(workLocationTypesDict.data)
      );
    }
  }, [workLocationTypesDict.data]);

  useLayoutEffect(() => {
    if (deptProjList?.data) {
      dispatch(dictionaryActions.setDeptProjList(deptProjList.data));
    }
  }, [deptProjList.data]);

  useLayoutEffect(() => {
    if (attendanceReasonDict?.data) {
      dispatch(
        dictionaryActions.setAttendanceReasonDict(attendanceReasonDict.data)
      );
    }
  }, [attendanceReasonDict.data]);

  useLayoutEffect(() => {
    if (attendanceStatusesDict?.data) {
      dispatch(
        dictionaryActions.setAttendanceStatusesDict(attendanceStatusesDict.data)
      );
    }
  }, [attendanceStatusesDict.data]);

  useLayoutEffect(() => {
    if (attendanceTypeDict?.data) {
      dispatch(
        dictionaryActions.setAttendanceTypeDict(attendanceTypeDict.data)
      );
    }
  }, [attendanceTypeDict.data]);
};

export default useDictionaryLoader;
